import React from 'react';

export interface MicroSoftSquareIconProps extends React.SVGProps<SVGSVGElement> {
  /** Width/height in px (defaults to 24). */
  size?: number | string;
  /** Optional accessible name; when provided the icon is exposed to assistive tech. */
  title?: string;
}

/**
 * Microsoft four-square mark on a white rounded tile, tinted with the Bombiix
 * palette rather than Microsoft's brand colours. Used for in-product decoration
 * (the connected-directory chip) — never for a Microsoft-branded sign-in button,
 * which must carry the official logo.
 * @param props - component props (any valid SVG attribute may also be passed).
 * @returns the square Microsoft mark SVG element.
 */
function MicroSoftSquareIcon({ size = 24, className = '', title, ...rest }: MicroSoftSquareIconProps) {
  const accessibilityProps = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': true, focusable: false };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...accessibilityProps}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <rect width="24" height="24" rx="4" fill="var(--color-white)" />
      <path
        d="M9.5 2H4.5C3.39543 2 2.5 2.89543 2.5 4V9C2.5 10.1046 3.39543 11 4.5 11H9.5C10.6046 11 11.5 10.1046 11.5 9V4C11.5 2.89543 10.6046 2 9.5 2Z"
        fill="var(--color-red)"
      />
      <path
        d="M9.5 12H4.5C3.39543 12 2.5 12.8954 2.5 14V19C2.5 20.1046 3.39543 21 4.5 21H9.5C10.6046 21 11.5 20.1046 11.5 19V14C11.5 12.8954 10.6046 12 9.5 12Z"
        fill="var(--color-blue)"
      />
      <path
        d="M19.5 2H14.5C13.3954 2 12.5 2.89543 12.5 4V9C12.5 10.1046 13.3954 11 14.5 11H19.5C20.6046 11 21.5 10.1046 21.5 9V4C21.5 2.89543 20.6046 2 19.5 2Z"
        fill="var(--color-green)"
      />
      <path
        d="M19.5 12H14.5C13.3954 12 12.5 12.8954 12.5 14V19C12.5 20.1046 13.3954 21 14.5 21H19.5C20.6046 21 21.5 20.1046 21.5 19V14C21.5 12.8954 20.6046 12 19.5 12Z"
        fill="var(--color-amber)"
      />
    </svg>
  );
}

/** Pure presentational icon — memoised so it never re-renders unless its props change. */
export default React.memo(MicroSoftSquareIcon);
